import { get, post, put, del } from '../AxiosConfig';
import { withCache, cacheInvalidate, cacheInvalidateAll } from './profitabilityCache';

// =============================================================================
// Cache keys — helpers to build a consistent key
// =============================================================================
const k = (prefix, parts) =>
  `${prefix}:${parts.map(p => (p === null || p === undefined || p === '' ? '_' : p)).join(':')}`;

// =============================================================================
// Profitability report — short reuse; every relevant mutation invalidates it.
// =============================================================================
const getProfitabilityReport = withCache(
  (payload, config = {}) => post('/profitability/monthly-report', payload, config),
  ({ companyId, month, year }) => k('profitabilityReport', [companyId, month, year]),
  30 * 1000,
);

// One authenticated invocation loads the report and both summary periods.
const getMonthlyScreen = withCache(
  (payload, config = {}) => post('/profitability/monthly-report/screen', payload, config),
  ({ companyId, month, year, actualViewMode = 'Service' }) =>
    k('monthlyScreen', [companyId, month, year, actualViewMode, new Date().getFullYear(), new Date().getMonth()]),
  30 * 1000,
);

// =============================================================================
// Employee salary — fetch
// =============================================================================
const getSalariesRaw = ({ companyId, month, year }) =>
  get(`/profitability/salary?companyId=${companyId}&month=${month}&year=${year}`);

const getSalaries = withCache(
  getSalariesRaw,
  ({ companyId, month, year }) => k('salaries', [companyId, month, year])
);

const saveSalary = async (payload) => {
  const res = await post('/profitability/salary', payload);
  cacheInvalidate('salaries:');
  return res;
};

// =============================================================================
// VAT Summary (Iteration 3)
// =============================================================================
const getVatSummary = ({ companyId, month, year }) =>
  get(`/profitability/vat-summary?companyId=${companyId}&month=${month}&year=${year}`);

// =============================================================================
// Financial Summary (Iteration 5) — 8 קלפי סיכום רווחיות
// Phase 1.E.1 — נושא 2 (C3): פרמטר actualViewMode מאפשר תצוגת בלוק B
//   'Service' (ברירת מחדל, חשבונאי) או 'Reception' (תזרימי, לפי קבלה)
// =============================================================================
const getFinancialSummaryRaw = ({ companyId, month, year, actualViewMode = 'Service' }, config = {}) => {
  let url = `/profitability/financial-summary?companyId=${companyId}&month=${month}&year=${year}`;
  if (actualViewMode && actualViewMode !== 'Service') {
    url += `&actualViewMode=${encodeURIComponent(actualViewMode)}`;
  }
  return get(url, config);
};
const getFinancialSummary = withCache(
  getFinancialSummaryRaw,
  ({ companyId, month, year, actualViewMode = 'Service' }) => k('financialSummary', [companyId, month, year, actualViewMode]),
  30 * 1000,
);

// =============================================================================
// Provisions Schedule (Iteration 4) — לוח "כסף צבוע" להפרשות חודשיות
// =============================================================================
const getProvisionsSchedule = ({ companyId, month, year }) =>
  get(`/profitability/provisions?companyId=${companyId}&month=${month}&year=${year}`);

// =============================================================================
// Employee lifecycle events (Iteration 2)
// =============================================================================
const getEmployeeHistory = ({ companyId, userId }) =>
  get(`/profitability/employee-history?companyId=${companyId}&userId=${userId}`);

const saveEmployeeEvent = async (payload) => {
  const res = await post('/profitability/employee-event', payload);
  cacheInvalidate('salaries:');
  return res;
};

// =============================================================================
// Expenses — fetch (monthly + range search)
// =============================================================================
// Phase 1.E.1 נושא 2 (C3): viewMode = 'Service' (ברירת מחדל) או 'Reception'
const getExpensesRaw = ({ companyId, month, year, startYearMonth = null, endYearMonth = null, viewMode = 'Service' }, config = {}) => {
  let url = `/profitability/expenses?companyId=${companyId}&month=${month}&year=${year}`;
  if (startYearMonth != null) url += `&startYearMonth=${startYearMonth}`;
  if (endYearMonth   != null) url += `&endYearMonth=${endYearMonth}`;
  if (viewMode && viewMode !== 'Service') url += `&viewMode=${encodeURIComponent(viewMode)}`;
  return get(url, config);
};

const getExpenses = withCache(
  getExpensesRaw,
  ({ companyId, month, year, startYearMonth = null, endYearMonth = null, viewMode = 'Service' }) =>
    k('expenses', [companyId, month, year, startYearMonth, endYearMonth, viewMode]),
  30 * 1000,
);

const saveExpense = async (payload) => {
  const res = await post('/profitability/expense', payload);
  cacheInvalidate(['expenses:', 'expenseCategories:']);
  return res;
};

// =============================================================================
// Invoices
// =============================================================================
const getInvoicesRaw = ({ companyId, month = 0, year = 0, missingOnly = false,
                         startYearMonth = null, endYearMonth = null, searchTerm = null }) => {
  let url = `/profitability/invoices?companyId=${companyId}&month=${month}&year=${year}&missingOnly=${missingOnly ? 1 : 0}`;
  if (startYearMonth != null) url += `&startYearMonth=${startYearMonth}`;
  if (endYearMonth   != null) url += `&endYearMonth=${endYearMonth}`;
  if (searchTerm)             url += `&searchTerm=${encodeURIComponent(searchTerm)}`;
  return get(url);
};

const getInvoices = withCache(
  getInvoicesRaw,
  ({ companyId, month = 0, year = 0, missingOnly = false,
     startYearMonth = null, endYearMonth = null, searchTerm = null }) =>
    k('invoices', [companyId, month, year, missingOnly ? 1 : 0, startYearMonth, endYearMonth, searchTerm])
);

const saveInvoice = async (payload) => {
  const res = await post('/profitability/invoice', payload);
  cacheInvalidate(['invoices:', 'paymentSchedule:']);
  return res;
};

// =============================================================================
// Location groups
// =============================================================================
const getLocationGroupsRaw = ({ companyId }) =>
  get(`/profitability/location-groups?companyId=${companyId}`);

const getLocationGroups = withCache(
  getLocationGroupsRaw,
  ({ companyId }) => k('locationGroups', [companyId])
);

const saveLocationGroup = async (payload) => {
  const res = await post('/profitability/location-group', payload);
  cacheInvalidate(['locationGroups:', 'invoices:', 'paymentSchedule:']);
  return res;
};

// =============================================================================
// Payment schedule — הכבדה העיקרית! Cache חשוב
// =============================================================================
const getPaymentScheduleRaw = ({ companyId, month, year }) =>
  get(`/profitability/payment-schedule?companyId=${companyId}&month=${month}&year=${year}`);

const getPaymentSchedule = withCache(
  getPaymentScheduleRaw,
  ({ companyId, month, year }) => k('paymentSchedule', [companyId, month, year])
);

// =============================================================================
// Payment schedule override — שמירת עריכות לחודש בודד
// =============================================================================
const savePaymentScheduleOverride = async (payload) => {
  const res = await post('/profitability/payment-schedule-override', payload);
  // משפיע על תצוגת GetPaymentSchedule + GetInvoiceTracking (סכומי ריכוז מחושבים שם)
  cacheInvalidate(['paymentSchedule:', 'invoices:']);
  return res;
};

// =============================================================================
// Rebuild monthly stats — invalidate הכל
// =============================================================================
const rebuildMonthlyStats = async (payload) => {
  const res = await post('/profitability/rebuild-monthly', payload);
  cacheInvalidateAll();
  return res;
};

const updateBankCashFee = async (payload) => {
  const res = await post('/profitability/bank-cash-fee', payload);
  cacheInvalidate('paymentSchedule:');
  return res;
};

// =============================================================================
// Terminals for assignment
// =============================================================================
const getTerminalsForAssignmentRaw = ({ companyId }) =>
  get(`/profitability/terminals-for-assignment?companyId=${companyId}`);

const getTerminalsForAssignment = withCache(
  getTerminalsForAssignmentRaw,
  ({ companyId }) => k('terminalsForAssignment', [companyId])
);

const assignGroupTerminals = async (payload) => {
  const res = await post('/profitability/assign-group-terminals', payload);
  cacheInvalidate(['locationGroups:', 'terminalsForAssignment:', 'invoices:', 'paymentSchedule:']);
  return res;
};

// =============================================================================
// Expense sources
// =============================================================================
const getExpenseSourcesRaw = ({ companyId, includeInactive = false }, config = {}) =>
  get(`/profitability/expense-sources?companyId=${companyId}&includeInactive=${includeInactive ? 1 : 0}`, config);

const getExpenseSources = withCache(
  getExpenseSourcesRaw,
  ({ companyId, includeInactive = false }) =>
    k('expenseSources', [companyId, includeInactive ? 1 : 0])
);

const saveExpenseSource = async (payload) => {
  const res = await post('/profitability/expense-source', payload);
  cacheInvalidate(['expenseSources:', 'expenseCategories:']);
  return res;
};

// =============================================================================
// Expense categories
// =============================================================================
const getExpenseCategoriesRaw = ({ companyId }) =>
  get(`/profitability/expense-categories?companyId=${companyId}`);

const getExpenseCategories = withCache(
  getExpenseCategoriesRaw,
  ({ companyId }) => k('expenseCategories', [companyId])
);

const renameExpenseCategory = async ({ companyId, oldName, newName }) => {
  const res = await put('/profitability/expense-category', { companyId, oldName, newName });
  cacheInvalidate(['expenseSources:', 'expenseCategories:']);
  return res;
};

// V2 — CRUD מלא לקטגוריות (FEAT-012, ExpenseCategory table)
const getExpenseCategoriesV2 = withCache(
  ({ companyId, statusFilter = 'active' }, config = {}) =>
    get('/profitability/expense-categories-v2?companyId=' + companyId + '&statusFilter=' + statusFilter, config),
  ({ companyId, statusFilter = 'active' }) => k('expenseCategories', [companyId, statusFilter]),
);

const saveExpenseCategory = async (payload) => {
  const res = await post('/profitability/expense-category-v2', payload);
  cacheInvalidate(['expenseSources:', 'expenseCategories:']);
  return res;
};

// =============================================================================
// Missing fixed expenses
// =============================================================================
const getMissingFixedExpensesRaw = ({ companyId, month, year }) =>
  get(`/profitability/expenses/missing-fixed?companyId=${companyId}&month=${month}&year=${year}`);

const getMissingFixedExpenses = withCache(
  getMissingFixedExpensesRaw,
  ({ companyId, month, year }) => k('missingFixedExpenses', [companyId, month, year])
);

// =============================================================================
// File upload / URL — לא ב-cache (עסקאות ו-SAS URL מתחדש)
// =============================================================================
const uploadExpenseFile = async ({ companyId, fileContent, fileName, year, month }) => {
  const res = await post('/profitability/expense/upload', { companyId, fileContent, fileName, year, month });
  cacheInvalidate('expenses:');
  return res;
};

const getExpenseFileUrl = ({ id, companyId }) =>
  get(`/profitability/expense/${id}/url?companyId=${companyId}`);

// =============================================================================
// Invoice scan / queue
// =============================================================================
// validateStatus: מאפשר ל-409 (duplicate) לעבור כ-response רגיל
// ולא לגרום ל-toast גלובלי של axios interceptor — הטיפול ב-409 נעשה ב-component
const uploadInvoiceFile = async (payload) => {
  const res = await post('/profitability/invoice/upload', payload, {
    validateStatus: (status) => status < 500,
  });
  // ביטול cache של invoices וגם של התור (כדי ש-fetchQueue ימשוך state טרי)
  cacheInvalidate(['invoices:', 'invoiceQueue:']);
  return res;
};

const getInvoiceQueueRaw = ({ companyId }) =>
  get(`/profitability/invoice/queue?companyId=${companyId}`);

const getInvoiceQueue = withCache(
  getInvoiceQueueRaw,
  ({ companyId }) => k('invoiceQueue', [companyId])
);

const assignInvoiceFile = async (payload) => {
  // validateStatus: מאפשר ל-400 (ולידציה / שיוך כפול) לעבור כ-response רגיל
  // ולא לגרום ל-toast גלובלי כפול — הטיפול בהודעת השגיאה נעשה ב-component
  const res = await post('/profitability/invoice/assign', payload, {
    validateStatus: (status) => status < 500,
  });
  cacheInvalidate(['invoices:', 'invoiceQueue:']);
  return res;
};

const getInvoiceFileUrl = ({ id, companyId }) =>
  get(`/profitability/invoice/${id}/url?companyId=${companyId}`);

const deleteInvoiceQueueItem = async ({ id, companyId }) => {
  const res = await del(`/profitability/invoice/queue/${id}?companyId=${companyId}`);
  cacheInvalidate('invoiceQueue:');
  return res;
};

const deleteAllInvoiceQueue = async ({ companyId }) => {
  const res = await del(`/profitability/invoice/queue/all?companyId=${companyId}`);
  cacheInvalidate('invoiceQueue:');
  return res;
};

const assignInvoiceAsExpense = async (payload) => {
  // validateStatus: מאפשר ל-400 (ולידציה / שיוך כפול) לעבור כ-response רגיל
  // ולא לגרום ל-toast גלובלי כפול — הטיפול בהודעת השגיאה נעשה ב-component
  const res = await post('/profitability/invoice/assign-expense', payload, {
    validateStatus: (status) => status < 500,
  });
  cacheInvalidate(['invoices:', 'invoiceQueue:', 'expenses:']);
  return res;
};

const sendInvoiceToAccountant = (payload) =>
  post('/profitability/invoice/send-to-accountant', payload);

// Recognition presets — public to any authenticated user (ניהול: /sysadmin)
const getRecognitionPresets = withCache(
  (config = {}) => get('/profitability/recognition-presets', config),
  () => 'recognitionPresets:all',
);

// Iteration 5b — VAT Policy Modes (public read; ניהול: /sysadmin)
const getVatPolicyModes = () => get('/profitability/vat-policy-modes');

// Iteration 5b — Company VAT Policy (per-company)
const getCompanyVatPolicy = (companyId) =>
  get(`/profitability/company-vat-policy?companyId=${companyId}`);

const saveCompanyVatPolicy = (payload) =>
  post('/profitability/company-vat-policy', payload);

// =============================================================================
// Revenue Invoices (25/05/2026) — חשבוניות הכנסה ידניות
// מטרה: לקלוט חשבוניות שאינן דמי שימוש (חיובים חד-פעמיים, התקנות, עמלות מיוחדות).
//        משולב ב-Actual_GrossRevenue ב-GetFinancialSummary.
// =============================================================================
const getRevenueInvoicesRaw = ({ companyId, month, year, startYearMonth = null, endYearMonth = null }) => {
  let url = `/profitability/revenue-invoices?companyId=${companyId}&month=${month}&year=${year}`;
  if (startYearMonth != null) url += `&startYearMonth=${startYearMonth}`;
  if (endYearMonth   != null) url += `&endYearMonth=${endYearMonth}`;
  return get(url);
};

const getRevenueInvoices = withCache(
  getRevenueInvoicesRaw,
  ({ companyId, month, year, startYearMonth = null, endYearMonth = null }) =>
    k('revenueInvoices', [companyId, month, year, startYearMonth, endYearMonth])
);

const saveRevenueInvoice = async (payload) => {
  const res = await post('/profitability/revenue-invoice', payload);
  cacheInvalidate('revenueInvoices:');
  return res;
};

const uploadRevenueInvoiceFile = async ({ companyId, fileContent, fileName, year, month }) => {
  const res = await post('/profitability/revenue-invoice/upload', { companyId, fileContent, fileName, year, month });
  cacheInvalidate('revenueInvoices:');
  return res;
};

const getRevenueInvoiceFileUrl = ({ id, companyId }) =>
  get(`/profitability/revenue-invoice/${id}/url?companyId=${companyId}`);

// =============================================================================
// Exports
// =============================================================================
const profitabilityService = {
  getMonthlyScreen,
  getProfitabilityReport,
  getRecognitionPresets,
  getVatPolicyModes,
  getCompanyVatPolicy,
  saveCompanyVatPolicy,
  getSalaries,
  saveSalary,
  getEmployeeHistory,
  saveEmployeeEvent,
  getVatSummary,
  getProvisionsSchedule,
  getFinancialSummary,
  getExpenses,
  saveExpense,
  getExpenseSources,
  saveExpenseSource,
  getMissingFixedExpenses,
  uploadExpenseFile,
  getExpenseFileUrl,
  getInvoices,
  saveInvoice,
  getLocationGroups,
  saveLocationGroup,
  getPaymentSchedule,
  savePaymentScheduleOverride,
  rebuildMonthlyStats,
  updateBankCashFee,
  getTerminalsForAssignment,
  assignGroupTerminals,
  uploadInvoiceFile,
  getInvoiceQueue,
  assignInvoiceFile,
  getInvoiceFileUrl,
  deleteInvoiceQueueItem,
  deleteAllInvoiceQueue,
  assignInvoiceAsExpense,
  getExpenseCategories,
  renameExpenseCategory,
  getExpenseCategoriesV2,
  saveExpenseCategory,
  sendInvoiceToAccountant,
  getRevenueInvoices,
  saveRevenueInvoice,
  uploadRevenueInvoiceFile,
  getRevenueInvoiceFileUrl,
};

export default profitabilityService;
